import {
  OnInit,
  Component,
  Input,
  Inject,
  EventEmitter,
  Output
} from '@angular/core';
import { Store } from '@ngrx/store';
import { IdynamicFormItem } from '../../../../shared/dynamic-form/interfaces/IdynamicFormItem';
import { InterpolateService } from '../../../../shared/services/interpolate.service';
import { TranslateService } from '@ngx-translate/core';
import { EditableAnnotationService } from '../../services/editable-annotation.service';
import * as _ from 'lodash';
import { map, flatMap, take } from 'rxjs/operators';
import { ConfigurationStoreHandler } from '../../../../configuration/services/configuration-store-handler.service';
import { selectCurrentDiagnosticOrder } from '../../store/diagnostic-order.selector';
import { IStepConfiguration } from '../../../../configuration/interfaces/step-configuration.interface';
import { IConfigurationsMap } from '../../../../configuration/interfaces/definition.interface';
import {AberrationsService} from "@ispm/aberrations/services/aberrations.service";
import { AuthenticationService } from '@ispm/user/services/authentication.service';

@Component({
  selector: 'gen-editable-annotation',
  templateUrl: './editable-annotations.component.html',
  styleUrls: ['./editable-annotations.component.scss']
})
export class EditableAnnotationsComponent implements OnInit {

  // Setter and getter are used here to allow for content updates
  @Input() set annotations(v: AnnotationClass) {
    this._annotations = v;

    // Test configuration must be present before re-initializing the component
    if (this.testConfig) {
      this.init();
    }
  }

  get annotations() {
    return this._annotations;
  }
  @Input() currentModality: string;

  @Input() aberration: any;

  // Setter and getter are used here to allow for content updates
  @Input() set testConfig(v: any) {
    this._testConfig = v;

    // Annotations data must be present before re-initializing the component
    if (this.annotations) {
      this.init();
    }
  }

  get testConfig() {
    return this._testConfig;
  }

  @Output() annotationUpdated: EventEmitter<any> = new EventEmitter();

  formData: { [key: string]: string };

  formConfig: Array<IdynamicFormItem>;
  formOverrides: { [key: string]: any };
  formEditable: boolean = false;

  private _onSubmit: OnSubmitAction;
  private _editableAnnotationsConfig: EditableAnnotations;

  private _translationPrefix: string = 'annotation';

  private _testConfig: any;
  private _annotations: AnnotationClass;

  constructor(
    private _store: Store<any>,
    private _editableAnnotationService: EditableAnnotationService,
    private _interpolateService: InterpolateService,
    private _configurationStoreHandler: ConfigurationStoreHandler,
    private _aberrationService: AberrationsService,
    private _authService: AuthenticationService,
    private _translateService: TranslateService
  ) { }

  ngOnInit() {
    this.getStepData()
      .pipe(
        map(config => {
          this.formEditable = this.isEditable(config);
          this.formOverrides = this.buildOverrides(config.stepConfig);
        })
      )
      .subscribe();
  }

  init() {
    this._editableAnnotationsConfig = this.testConfig['editableAnnotations'];

    const classConfig = this._editableAnnotationsConfig.annotationClasses[
      this.annotations.preTranslationCategory
    ];

    this._onSubmit = this.buildOnSubmitAction(classConfig);

    const tempConfig = [];
    const tempData = { };
    this.annotations.annotations.forEach(annotation => {
      let config;

      if (classConfig.annotations[annotation.name]) {
        config = classConfig.annotations[annotation.name];
      } else {
        config = this._editableAnnotationsConfig.defaultFieldConfig;
      }

      tempConfig.push(
        this.buildFormConfigItem(
          annotation,
          config
        )
      );

      let annotationValue: string;
      if (config.translateValue) {
        annotationValue = this.getTranslatedValue(annotation.value);
      } else {
        annotationValue = annotation.value;
      }

      tempData[annotation.name] = annotationValue;
    });
    // Set the form configuration and data in one go instead of building them as the initialization progresses
    this.formData = tempData;
    this.formConfig = tempConfig;
  }

  isEditable(config): boolean {
    return (
      config.stepConfig &&
      config.stepConfig.fieldsValidation &&
      config.stepConfig.fieldsValidation.isEditable &&
      config.diagnosticOrder.performer == this._authService.user.info.id
    );
  }

  getTranslatedValue(value: string): string {
    if (!value) {
      return value;
    }
    const translation = this._translateService.instant(`${this._translationPrefix}.${value}`, value);
    return `${this._translationPrefix}.${value}` !== translation ? translation : value;
  }

  getStepData() {
    return this._configurationStoreHandler
      .getConfigurationForCurrentStep()
      .pipe(
        take(1),
        flatMap(stepConfig => {
          return this._store.select(selectCurrentDiagnosticOrder).pipe(
            take(1),
            map(diagnosticOrder => {
              return {
                diagnosticOrder,
                stepConfig
              };
            })
          );
        })
      );
  }

  buildOverrides(
    stepCofnig: IStepConfiguration<IConfigurationsMap>
  ): { [key: string]: any } {
    const formOverrides = { };
    if (
      !(
        stepCofnig &&
        stepCofnig.fieldsValidation &&
        stepCofnig.fieldsValidation.override
      )
    ) {
      return formOverrides;
    }

    stepCofnig.fieldsValidation.override.forEach(override => {
      // Overrides may not be initialized yet (new popup)
      if (!this.formOverrides || !(override.form in this.formOverrides)) {
        formOverrides[override.form] = { };
      }
      formOverrides[override.form][override.field] = override;
    });

    return formOverrides;
  }

  buildOnSubmitAction(onSubmitConfig: EditableAnnotationClass): OnSubmitAction {
    return onSubmitConfig.onSubmit;
  }

  buildFormConfigItem(annotation: Annotation, formConfig: any): any {
    const name = this._interpolateService.interpolate(formConfig.name, {
      annotation
    });
    const label = this._interpolateService.interpolate(formConfig.label, {
      annotation
    });
    const translatedlabel = this.getTranslatedValue(label);
    return {
      ...formConfig,
      name: name,
      label: translatedlabel
    };
  }

  onSave($event: Updated): void {
    let annotationResultIndex: number;

    let annotationToUpdate: AnnotationResult;

    $event.updated.forEach(updatedField => {
      if (!!this.annotations.annotationResultList) {
        this.annotations.annotationResultList.forEach(
          (annoatationResult, index) => {
            const annotationIndex = _.findIndex(
              annoatationResult.annotations,
              annotation => annotation.name == updatedField
            );
            if (annotationIndex > -1) {
              annotationToUpdate = annoatationResult;
              annotationToUpdate.annotations[annotationIndex].value =
                $event.form[updatedField];
              annotationResultIndex = index;
            }
          }
        );
      }
    });

    if (this._onSubmit.remote) {

      //Check case where we should skip this submit (for example on form reflect)
      let listOfFieldsToUpdateAfterStrippingIgnoredFields = this._onSubmit.ignoreList && $event.updated.filter((item) => !this._onSubmit.ignoreList.includes(item));
      if(listOfFieldsToUpdateAfterStrippingIgnoredFields && listOfFieldsToUpdateAfterStrippingIgnoredFields.length <= 0){
        return;
      }

      this.annotationUpdated.emit(true);

      if (this._onSubmit.remote == '/aberration') {
        this._editableAnnotationService
          .updateAberration(this._onSubmit.remote, {id:this.aberration.id, updated: $event.form})
          .subscribe((updatedAberration) => {
            this._aberrationService.aberrationUpdateSubject.next({id:this.aberration.id, updated: $event});
            this.annotationUpdated.emit(false);
          });
      } else {
        this._editableAnnotationService
          .updateAnnotations(this._onSubmit.remote, annotationToUpdate)
          .pipe(
            map(annotationResult => {
              this.annotations.annotationResultList[
                annotationResultIndex
                ] = annotationResult;
            })
          )
          .subscribe(() => {
            this.annotationUpdated.emit(false);
          });
      }
    }
  }

}

export interface Updated {
  form: { [key: string]: string };
  updated: Array<string>;
}

interface AnnotationClass {
  preTranslationCategory: string;
  annotations: Array<Annotation>;
  annotationResultList: Array<AnnotationResult>;
}

interface Annotation {
  annotationSource: string;
  name: string;
  value: string;
}

export interface AnnotationResult {
  id: string;
  annotationSource: string;
  annotationSourceClass: string;
  annotations: Array<Annotation>;
}

interface EditableAnnotations {
  defaultFieldConfig: any;
  annotationClasses?: {
    [key: string]: EditableAnnotationClass;
  };
}

interface EditableAnnotationClass {
  onSubmit: OnSubmitAction;
  annotations: {
    [key: string]: any;
  };
}

interface OnSubmitAction {
  remote?: string;
  ignoreList?: string[]
}
